import {
  consignmentFeePolicyIsValid,
  evaluateConsignmentSaleFees,
  consignmentFeeCalculationMatches,
} from './consignment-fees';
import type { ConsignmentFeeChargeLine } from './consignment-charges';
import type { ConsignmentSaleAllocation, ConsignmentSaleLifecycle } from './consignment';

export const CONSIGNMENT_STATEMENT_MAX_LINES = 50;
export interface ConsignmentBankReceiptReview {
  reference: string;
  at: string;
  actor: string;
}
export function consignmentBankReceiptReference(value: unknown): string | undefined {
  if (
    typeof value !== 'string' ||
    value.length > 500 ||
    Array.from(value).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)
  )
    return undefined;
  const reference = value.trim();
  return reference.length >= 3 ? reference : undefined;
}
export type ConsignmentStatementStatus = 'DRAFT' | 'APPROVED' | 'VOIDED';
export interface ConsignmentStatementClaim {
  statementId: string;
  status: 'DRAFT' | 'APPROVED';
}
export type ConsignmentStatementLine = Pick<
  ConsignmentSaleAllocation,
  | 'allocationId'
  | 'cardId'
  | 'orderId'
  | 'lineItemId'
  | 'consignorId'
  | 'agreementId'
  | 'title'
  | 'externalListingId'
  | 'soldAt'
  | 'payoutEligibleAt'
  | 'currency'
  | 'grossAmountMinor'
  | 'commissionBps'
  | 'fixedFeeMinor'
  | 'feePolicy'
  | 'feeCalculation'
> & { lifecycle: ConsignmentSaleLifecycle };
export interface ConsignmentStatementTotals {
  grossAmountMinor: number;
  refundAmountMinor: number;
  commissionAmountMinor: number;
  fixedFeeMinor: number;
  estimatedProceedsMinor: number;
  unsoldFeeAmountMinor?: number;
}
export interface ConsignmentStatement {
  financialSummary?: import('./consignment-payments').ConsignmentFinanceSummary;
  statementId: string;
  consignorId: string;
  consignorName: string;
  status: ConsignmentStatementStatus;
  version: number;
  currency: 'USD';
  lines: ConsignmentStatementLine[];
  feeCharges?: ConsignmentFeeChargeLine[];
  totals: ConsignmentStatementTotals;
  createdAt: string;
  createdBy: string;
  approvedAt?: string;
  approvedBy?: string;
  bankReceiptReview?: ConsignmentBankReceiptReview;
  voidedAt?: string;
  voidedBy?: string;
  voidReason?: string;
}
export interface ConsignmentStatementEvent {
  action: 'CREATED' | 'APPROVED' | 'VOIDED';
  at: string;
  actor: string;
  version: number;
  reason?: string;
  bankReceiptReview?: ConsignmentBankReceiptReview;
}
export interface ConsignmentStatementDetail {
  statement: ConsignmentStatement;
  events: ConsignmentStatementEvent[];
  issues: Array<{ allocationId: string; reason: string }>;
}
export type ConsignmentStatementMutation =
  | {
      operation: 'CREATE_STATEMENT';
      statementId: string;
      consignorId: string;
      allocationIds: string[];
      feeChargeIds?: string[];
    }
  | {
      operation: 'APPROVE_STATEMENT';
      statementId: string;
      consignorId: string;
      version: number;
      feesReviewed: boolean;
      acknowledgeEstimates: boolean;
      bankReceiptReviewed?: boolean;
      bankReceiptReference?: string;
    }
  | {
      operation: 'VOID_STATEMENT';
      statementId: string;
      consignorId: string;
      version: number;
      reason: string;
    };

/** New schedules use one conserved calculator; legacy snapshots keep their historical rules. */
export function consignmentSaleFeeEvidenceBlock(
  sale: Pick<
    ConsignmentSaleAllocation,
    | 'feePolicy'
    | 'feeCalculation'
    | 'grossAmountMinor'
    | 'commissionBps'
    | 'fixedFeeMinor'
    | 'lifecycle'
  >,
): string | undefined {
  if (!sale.feePolicy)
    return sale.feeCalculation || sale.lifecycle?.feeCalculation
      ? 'Missing frozen fee policy'
      : undefined;
  const life = sale.lifecycle;
  if (!consignmentFeePolicyIsValid(sale.feePolicy) || !life) return 'Invalid frozen fee policy';
  try {
    const original = evaluateConsignmentSaleFees(sale.feePolicy, sale.grossAmountMinor);
    const expected = evaluateConsignmentSaleFees(
      sale.feePolicy,
      sale.grossAmountMinor,
      life.refundAmountMinor,
      Boolean(life.reversalReason),
    );
    if (
      !consignmentFeeCalculationMatches(sale.feeCalculation, original) ||
      sale.commissionBps !== original.commissionBps ||
      sale.fixedFeeMinor !== original.fixedFeeMinor ||
      !consignmentFeeCalculationMatches(life.feeCalculation, expected) ||
      life.netGrossAmountMinor !== expected.netGrossAmountMinor ||
      life.commissionAmountMinor !== expected.commissionAmountMinor ||
      life.fixedFeeMinor !== expected.fixedFeeMinor ||
      life.estimatedProceedsMinor !== expected.estimatedProceedsMinor
    )
      return 'Scheduled, applied and waived fees do not match frozen terms';
  } catch {
    return 'Invalid scheduled fee economics';
  }
  return undefined;
}

/** Eligibility is advisory in the browser; the API rechecks under transaction conditions. */
export function consignmentStatementBlock(
  sale: ConsignmentSaleAllocation,
  now: string,
  ownDraftId?: string,
): string | undefined {
  if (sale.buyerReturnHoldId) return 'Buyer return needs financial review';
  if (
    sale.statement &&
    !(sale.statement.statementId === ownDraftId && sale.statement.status === 'DRAFT')
  )
    return sale.statement.status === 'APPROVED'
      ? 'Reserved in approved statement'
      : 'Reserved in draft';
  if (sale.status !== 'PROVISIONAL')
    return sale.status === 'SETTLED' ? 'Already settled' : 'Reversed sale';
  const life = sale.lifecycle;
  if (!life) return 'Awaiting order reconciliation';
  if (life.state === 'REVIEW_REQUIRED' || life.reviewReasons.length)
    return 'Order or refund needs review';
  if (!['PROVISIONAL', 'PARTIALLY_REFUNDED'].includes(life.state)) return 'Reversed sale';
  const hold = Date.parse(sale.payoutEligibleAt);
  if (!Number.isFinite(hold) || !Number.isFinite(Date.parse(now))) return 'Invalid hold date';
  if (hold > Date.parse(now)) return 'Agreement hold not elapsed';
  if (
    !Number.isFinite(Date.parse(life.sourceModifiedAt)) ||
    !life.sourceFingerprint ||
    !Number.isSafeInteger(life.version) ||
    life.version < 1
  )
    return 'Invalid order evidence';
  if (sale.feePolicy)
    return sale.currency === 'USD' ? consignmentSaleFeeEvidenceBlock(sale) : 'Invalid currency';
  if (sale.feeCalculation || life.feeCalculation) return 'Missing frozen fee policy';
  const amounts = [
    sale.grossAmountMinor,
    sale.fixedFeeMinor,
    life.refundAmountMinor,
    life.netGrossAmountMinor,
    life.commissionAmountMinor,
    life.fixedFeeMinor,
    life.estimatedProceedsMinor,
  ];
  if (
    sale.currency !== 'USD' ||
    amounts.some((n) => !Number.isSafeInteger(n) || n < 0) ||
    !Number.isInteger(sale.commissionBps) ||
    sale.commissionBps < 0 ||
    sale.commissionBps > 10000 ||
    life.netGrossAmountMinor !== sale.grossAmountMinor - life.refundAmountMinor ||
    life.commissionAmountMinor !==
      Math.round((life.netGrossAmountMinor * sale.commissionBps) / 10000) ||
    life.fixedFeeMinor !== sale.fixedFeeMinor ||
    life.estimatedProceedsMinor !==
      Math.max(0, life.netGrossAmountMinor - life.commissionAmountMinor - life.fixedFeeMinor)
  )
    return 'Deductions need review';
  if (life.estimatedProceedsMinor <= 0) return 'No positive proceeds';
  return undefined;
}
